import { Component } from '@theme/component';

/**
 * Zeequa reviews: product filter chips and a "load more" button over a list
 * of review cards rendered by Liquid.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement[]} chips - Filter chips (data-filter holds the product name, or "all").
 * @property {HTMLElement[]} cards - Review cards (data-product holds the product name).
 * @property {HTMLButtonElement} [loadMore] - Optional "load more" button.
 * @property {HTMLElement} [empty] - Optional empty-state element.
 *
 * @extends Component<Refs>
 */
class ZeequaReviewsComponent extends Component {
  requiredRefs = ['cards'];

  #filter = 'all';
  #visible = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#visible = this.#pageSize;

    // On product pages, start on the current product's reviews when it has any.
    const initial = this.dataset.initialFilter;
    if (initial && this.refs.cards.some((card) => card.dataset.product === initial)) {
      this.#filter = initial;
    }

    this.#render();
  }

  get #pageSize() {
    const size = Number(this.dataset.pageSize);
    return Number.isFinite(size) && size > 0 ? size : 9;
  }

  /**
   * Called from a chip: on:click="/filter". The product name comes from the
   * chip's `data-filter` attribute ("all" shows everything).
   * @param {Event} event
   */
  filter(event) {
    const target = event?.target instanceof Element ? event.target.closest('[data-filter]') : null;
    this.#filter = target instanceof HTMLElement && target.dataset.filter ? target.dataset.filter : 'all';
    this.#visible = this.#pageSize;
    this.#render();
  }

  /** Called from the load more button: on:click="/loadMore" */
  loadMore() {
    this.#visible += this.#pageSize;
    this.#render();
  }

  #render() {
    const { cards, chips = [], loadMore, empty } = this.refs;
    let shown = 0;
    let matching = 0;

    for (const card of cards) {
      const matches = this.#filter === 'all' || card.dataset.product === this.#filter;
      if (matches) matching += 1;
      const visible = matches && shown < this.#visible;
      if (visible) shown += 1;
      card.hidden = !visible;
    }

    for (const chip of chips) {
      const active = (chip.dataset.filter || 'all') === this.#filter;
      chip.classList.toggle('zeequa-reviews__chip--active', active);
      chip.setAttribute('aria-pressed', String(active));
    }

    if (loadMore) loadMore.hidden = shown >= matching;
    if (empty) empty.hidden = matching > 0;
  }
}

if (!customElements.get('zeequa-reviews-component')) {
  customElements.define('zeequa-reviews-component', ZeequaReviewsComponent);
}
